import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, isToday
} from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  Plus, ChevronLeft, ChevronRight, Calendar, X, Clock, Edit2, Trash2,
  Users, Check, XCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import MultiUserSelect from './MultiUserSelect';
import UserAvatar from './UserAvatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = [
  { id: 'green', bg: 'bg-[#10B981]', text: 'text-white', light: 'bg-[#D1FAE5]' },
  { id: 'blue', bg: 'bg-[#3B82F6]', text: 'text-white', light: 'bg-[#DBEAFE]' },
  { id: 'red', bg: 'bg-[#EF4444]', text: 'text-white', light: 'bg-[#FEE2E2]' },
  { id: 'purple', bg: 'bg-[#8B5CF6]', text: 'text-white', light: 'bg-[#EDE9FE]' },
  { id: 'orange', bg: 'bg-[#F97316]', text: 'text-white', light: 'bg-[#FFEDD5]' }
];

const VACATION_COLORS = {
  approved: 'bg-[#10B981] text-white',
  pending: 'bg-[#F59E0B] text-white',
};

const MonthCalendar = ({ vacations = [], onMonthChange, title, subtitle } = {}) => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    color: 'green',
    all_day: true,
    invited_users: []
  });

  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const fetchEvents = useCallback(async () => {
    try {
      const month = format(currentMonth, 'MM');
      const year = format(currentMonth, 'yyyy');
      const response = await axios.get(`${API_URL}/events?month=${month}&year=${year}`, {
        headers: getAuthHeader()
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  }, [currentMonth, getAuthHeader]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (onMonthChange) onMonthChange(currentMonth);
  }, [currentMonth, onMonthChange]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      const next = subMonths(prev, 1);
      return next;
    });
  };
  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      const next = addMonths(prev, 1);
      return next;
    });
  };
  const handleToday = () => setCurrentMonth(new Date());

  const openCreateDialog = (date = null) => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      date: date ? format(date, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      start_time: '',
      end_time: '',
      color: 'green',
      all_day: true,
      invited_users: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      date: event.date,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      color: event.color || 'green',
      all_day: event.all_day,
      invited_users: Array.isArray(event.participants) ? event.participants.map(p => p.user_id) : []
    });
    setDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!formData.title.trim()) {
      toast.error(t('events.error'));
      return;
    }

    try {
      if (editingEvent) {
        await axios.put(`${API_URL}/events/${editingEvent.id}`, formData, { headers: getAuthHeader() });
        toast.success(t('events.updated'));
      } else {
        await axios.post(`${API_URL}/events`, formData, { headers: getAuthHeader() });
        toast.success(t('events.created'));
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error(t('events.error'));
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      await axios.post(
        `${API_URL}/events/${eventId}/rsvp`,
        { status },
        { headers: getAuthHeader() }
      );
      toast.success(status === 'accepted' ? t('events.rsvpAccepted') : t('events.rsvpDeclined'));
      fetchEvents();
      // Refresh editing event copy
      if (editingEvent && editingEvent.id === eventId) {
        const refreshed = await axios.get(`${API_URL}/events/${eventId}`, { headers: getAuthHeader() });
        setEditingEvent(refreshed.data);
      }
    } catch (error) {
      toast.error(t('events.error'));
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API_URL}/events/${eventId}`, { headers: getAuthHeader() });
      toast.success(t('events.deleted'));
      fetchEvents();
    } catch (error) {
      toast.error(t('events.error'));
    }
  };

  const getColorInfo = (colorId) => COLORS.find(c => c.id === colorId) || COLORS[0];

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(e => e.date === dateStr);
  };

  const getVacationsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return vacations.filter(v => v.start_date <= dateStr && v.end_date >= dateStr);
  };

  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const weekDaysEN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const displayWeekDays = i18n.language === 'de' ? weekDays : weekDaysEN;

  return (
    <div data-testid="month-calendar" className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-zinc-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-zinc-900 truncate">{title || t('events.title')}</h3>
            <p className="text-sm text-zinc-500 truncate">{subtitle || format(currentMonth, 'MMMM yyyy', { locale: getLocale() })}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handlePrevMonth} data-testid="calendar-prev-month-btn" className="rounded-full h-8 w-8 p-0 shrink-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleToday} data-testid="calendar-today-btn" className="rounded-full text-xs h-8 px-3 shrink-0">
            {t('events.today')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleNextMonth} data-testid="calendar-next-month-btn" className="rounded-full h-8 w-8 p-0 shrink-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button 
            onClick={() => openCreateDialog()} 
            data-testid="calendar-new-event-btn"
            className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full h-8 px-3 text-xs shrink-0"
          >
            <Plus className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{t('events.newEvent')}</span>
          </Button>
        </div>
      </div>

      {/* Week Days Header */}
      <div className="grid grid-cols-7 border-b border-zinc-200">
        {displayWeekDays.map((dayName, idx) => (
          <div key={dayName} className={`py-2 text-center text-[11px] sm:text-xs font-semibold ${idx >= 5 ? 'text-zinc-400' : 'text-zinc-600'}`}>
            {dayName}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days.map((date) => {
          const dayEvents = getEventsForDay(date);
          const dayVacations = getVacationsForDay(date);
          const isCurrentMonth = isSameMonth(date, currentMonth);
          const isTodayDate = isToday(date);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;

          return (
            <div 
              key={format(date, 'yyyy-MM-dd')}
              onClick={() => {
                if (dayEvents.length === 0) {
                  openCreateDialog(date);
                }
              }}
              className={`min-h-[80px] min-w-0 p-1 border-b border-r border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${
                !isCurrentMonth ? 'bg-zinc-50/50' : ''
              } ${isWeekend ? 'bg-zinc-50/30' : ''}`}
            >
              <div className={`text-right mb-1`}>
                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
                  isTodayDate 
                    ? 'bg-[#D93025] text-white' 
                    : isCurrentMonth 
                      ? 'text-zinc-700' 
                      : 'text-zinc-400'
                }`}>
                  {format(date, 'd')}
                </span>
              </div>
              
              {/* Vacations + Events */}
              <div className="space-y-0.5">
                {dayVacations.slice(0, 2).map(v => (
                  <div
                    key={v.id}
                    onClick={(e) => e.stopPropagation()}
                    className={`${VACATION_COLORS[v.status] || VACATION_COLORS.approved} text-[10px] px-1.5 py-0.5 rounded truncate`}
                    title={`${v.user_name} — ${t('leave.onVacation')}${v.substitute_name ? ` · ${t('leave.substituteFor')} ${v.substitute_name}` : ''}`}
                  >
                    {v.user_name}
                  </div>
                ))}
                {dayEvents.slice(0, Math.max(0, 3 - Math.min(dayVacations.length, 2))).map(event => {
                  const colorInfo = getColorInfo(event.color);
                  const participantCount = Array.isArray(event.participants) ? event.participants.length : 0;
                  return (
                    <div
                      key={event.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(event);
                      }}
                      className={`${colorInfo.bg} ${colorInfo.text} text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80 flex items-center gap-1`}
                    >
                      <span className="truncate flex-1">{event.title}</span>
                      {participantCount > 0 && (
                        <span className="flex items-center gap-0.5 opacity-90">
                          <Users className="w-2.5 h-2.5" />
                          {participantCount}
                        </span>
                      )}
                    </div>
                  );
                })}
                {(dayVacations.length + dayEvents.length) > 3 && (
                  <div className="text-[10px] text-zinc-500 px-1">
                    +{(dayVacations.length + dayEvents.length) - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="event-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white">
                <Calendar className="w-5 h-5" />
              </div>
              {editingEvent ? t('events.editEvent') : t('events.newEvent')}
            </DialogTitle>
            <p id="event-dialog-description" className="sr-only">
              {editingEvent ? t('events.editEvent') : t('events.newEvent')}
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Title */}
            <div>
              <Label htmlFor="event-title" className="text-sm font-medium">{t('events.eventTitle')}</Label>
              <Input
                id="event-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('events.eventTitlePlaceholder')}
                className="mt-1.5 h-10"
                data-testid="event-title-input"
              />
            </div>
            
            {/* Description */}
            <div>
              <Label htmlFor="event-desc" className="text-sm font-medium">{t('events.description')}</Label>
              <Textarea
                id="event-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('events.descriptionPlaceholder')}
                className="mt-1.5 resize-none"
                rows={2}
              />
            </div>
            
            {/* Date */}
            <div>
              <Label className="text-sm font-medium">{t('events.date')}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1.5 h-10"
              />
            </div>
            
            {/* All Day Switch */}
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">{t('events.allDay')}</Label>
              <Switch
                checked={formData.all_day}
                onCheckedChange={(checked) => setFormData({ ...formData, all_day: checked })}
              />
            </div>
            
            {/* Time (if not all day) */}
            {!formData.all_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">{t('events.startTime')}</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="mt-1.5 h-10"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">{t('events.endTime')}</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="mt-1.5 h-10"
                  />
                </div>
              </div>
            )}
            
            {/* Color */}
            <div>
              <Label className="text-sm font-medium">{t('events.color')}</Label>
              <div className="flex gap-2 mt-2">
                {COLORS.map(color => (
                  <button
                    key={color.id}
                    onClick={() => setFormData({ ...formData, color: color.id })}
                    className={`w-8 h-8 rounded-full ${color.bg} ${
                      formData.color === color.id ? 'ring-2 ring-offset-2 ring-zinc-400' : ''
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Invited Participants */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-zinc-500" />
                {t('events.invitedParticipants')}
              </Label>
              <div className="mt-2">
                <MultiUserSelect
                  users={users.filter(u => u.id !== user?.id)}
                  value={formData.invited_users}
                  onChange={(ids) => setFormData({ ...formData, invited_users: ids })}
                  placeholder={t('events.inviteUsers')}
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">{t('events.inviteHint')}</p>
            </div>

            {/* RSVP for invited users when editing */}
            {editingEvent && Array.isArray(editingEvent.participants) && editingEvent.participants.length > 0 && (
              <div className="border-t pt-4">
                <Label className="text-sm font-medium mb-2 block">{t('events.rsvpStatus')}</Label>
                <div className="space-y-2">
                  {editingEvent.participants.map((p) => {
                    const fullUser = users.find(u => u.id === p.user_id) || { id: p.user_id, name: p.user_name };
                    return (
                    <div key={p.user_id} className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <UserAvatar user={fullUser} size="md" />
                        <span className="text-sm font-medium text-zinc-800">{p.user_name}</span>
                      </div>
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          p.status === 'accepted'
                            ? 'bg-[#D1FAE5] text-[#047857]'
                            : p.status === 'declined'
                            ? 'bg-[#FEE2E2] text-[#D93025]'
                            : 'bg-zinc-200 text-zinc-600'
                        }`}
                      >
                        {p.status === 'accepted' && <Check className="w-3 h-3 inline mr-0.5" />}
                        {p.status === 'declined' && <XCircle className="w-3 h-3 inline mr-0.5" />}
                        {t(`events.rsvp.${p.status}`)}
                      </span>
                    </div>
                    );
                  })}
                </div>

                {/* RSVP buttons if current user is invited */}
                {editingEvent.participants.some(p => p.user_id === user?.id) && (
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRSVP(editingEvent.id, 'accepted')}
                      data-testid="rsvp-accept-btn"
                      className="flex-1 rounded-full text-[#047857] border-[#047857] hover:bg-[#D1FAE5]"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      {t('events.accept')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRSVP(editingEvent.id, 'declined')}
                      data-testid="rsvp-decline-btn"
                      className="flex-1 rounded-full text-[#D93025] border-[#D93025] hover:bg-[#FEE2E2]"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      {t('events.decline')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 pt-2">
            {editingEvent && (
              <Button 
                variant="outline" 
                onClick={() => {
                  handleDeleteEvent(editingEvent.id);
                  setDialogOpen(false);
                }}
                className="rounded-full px-4 text-red-600 hover:text-red-700 hover:bg-red-50 mr-auto"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('common.delete')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full px-6">
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSaveEvent}
              className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
            >
              {editingEvent ? t('common.save') : t('events.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthCalendar;
